<script setup lang="ts">
import type { PdpEcommerceBlockFitFinderRedesignProps } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockFitFinderRedesign.props'
import type {
  Category,
  SizeDetails,
} from '@design-system/components/Pdp/PdpFitFinder.props'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'

const props = withDefaults(
  defineProps<PdpEcommerceBlockFitFinderRedesignProps>(),
  {
    fitting: null,
  }
)
const emit = defineEmits<{
  onFitFinderClick: []
}>()

const { openDialog, closeDialog } = useDialog(
  SIDEBAR_IDS.pdpEcommerceBlockFitFinder
)

const { emitSizebayProductViewEvent } = useSizebayEvents()
const route = useRoute()

const noSizeBay = computed(() => {
  if (process.server) return false
  return route.query.noSizeBay === 'true'
})

const testId = computed(() => (!noSizeBay.value ? 'fit-finder' : ''))

onBeforeMount(async () => {
  if (!props.hideSidebar && !noSizeBay.value) {
    await useSizebay()
    emitSizebayProductViewEvent(props.productId, props.productTitle)
  }
})

const {
  sizeGridData: sizeData,
  productCategory,
  countries,
} = await useFitFinder(props.sizeCodeId ?? '', props.mainCategory)

const handlePrimaryClick = () => {
  closeDialog()
}
const handleFitFinderClick = () => {
  setTimeout(() => {
    const sizeBayModalOk = document
      .querySelector('html')
      ?.classList.contains('vfr-modal-active')
    if (!sizeBayModalOk) {
      openDialog()
    }
  }, 300)
  emit('onFitFinderClick')
}
</script>

<template>
  <div class="flex w-full items-center justify-between gap-2">
    <p>
      <span v-if="customLabel" class="text-medium-6">{{ customLabel }}</span>
      <template v-else>
        <span v-if="fitting" class="text-medium-6 pr-1">{{
          `${$ts('pdp.fitScale.fitLabel')}:`
        }}</span>
        <span v-if="fitting" class="text-light-6">{{
          $ts(`pdp.fitScale.fit.${fitting}`)
        }}</span>
      </template>
    </p>
    <button
      type="button"
      class="text-light-6 gap-primitives-1 underline-effect-default flex items-center"
      :data-testid="testId"
      :aria-label="$ts('accessibility.openFitFinder')"
      aria-haspopup="dialog"
      @click="handleFitFinderClick"
    >
      <span class="max-[1439px]:hidden"
        >{{ $ts('pdp.fitFinder.ctaPrompt') }}
      </span>
      <span class="text-book-6 text-effect underline">{{
        $ts('pdp.fitFinder.ctaLink')
      }}</span>
      <DefaultIconsSizes class="size-6 shrink-0" aria-hidden="true" />
    </button>
  </div>
  <OrganismsSidebarSlide
    v-if="!hideSidebar"
    :id="SIDEBAR_IDS.pdpEcommerceBlockFitFinder"
    :header-props="{
      titleText: $ts('pdp.fitFinder.label'),
    }"
    @primary-click="handlePrimaryClick"
  >
    <template #body>
      <PdpFitFinder
        :category="productCategory as unknown as Category"
        :countries="countries"
        :sizes-details="sizeData as unknown as SizeDetails[]"
        :side-slide-description="props.sideSlideDescription"
        :size-code-id="sizeCodeId"
      />
    </template>
  </OrganismsSidebarSlide>
</template>
